/**
 * /solutions/marketing — shared data for the campaign story.
 *
 * One running example everywhere: the customer-branded link
 * `go.acme.com/spring-sale`. rii.link only ever appears as the product
 * (app URL, brand copy) — never as the example short domain.
 *
 * Channel color coding is systematic page-wide (hero conveyor, rail measure
 * bars, launch cards, dashboard): newsletter=blue, instagram-bio=violet,
 * flyer-qr=green, partner=amber; the hero pool adds cyan for paid/press.
 *
 * Numbers are one consistent story: total 3,606 (= 2,628 clicks + 978 scans),
 * channels 1,284 / 942 / 978 / 402. Change them together or not at all.
 */

import type { RoadmapId } from "@/lib/roadmap";

export const DATA_BLUE = "var(--color-data-blue)";
export const DATA_VIOLET = "var(--color-data-violet)";
export const DATA_GREEN = "var(--color-data-green)";
export const DATA_AMBER = "var(--color-data-amber)";
export const DATA_CYAN = "var(--color-data-cyan)";

/** The campaign totals — keep every appearance in sync. */
export const TOTAL_EVENTS = 3606;
export const TOTAL_FMT = "3,606";

/** The scattered pre-rii artifacts the hero conveyor cycles through. */
export interface HeroArtifact {
  tag: string;
  value: string;
  color: string;
}

export const HERO_POOL: HeroArtifact[] = [
  { tag: "newsletter", value: "bit.ly/3xK9pQz", color: DATA_BLUE },
  { tag: "bio", value: "linktr.ee/acme?lt=88", color: DATA_VIOLET },
  { tag: "print", value: "flyer_v2_FINAL.pdf", color: DATA_GREEN },
  { tag: "spreadsheet", value: "utm=Spring_SALE_v2", color: DATA_AMBER },
  { tag: "paid", value: "?fbclid=IwAR2x…", color: DATA_CYAN },
  { tag: "webinar", value: "zoom.us/j/82449?p=x8", color: DATA_VIOLET },
  { tag: "email sig", value: "mailchi.mp/acme/promo", color: DATA_BLUE },
  { tag: "podcast", value: "pod.link/acme?ep=41", color: DATA_AMBER },
  { tag: "sms", value: "a.co/8Xq2p", color: DATA_GREEN },
  { tag: "press", value: "prn.to/3kQz9", color: DATA_CYAN },
  { tag: "youtube", value: "youtu.be/x8Kq?si=88", color: DATA_BLUE },
  { tag: "partner", value: "partner.io/acme-20", color: DATA_VIOLET },
];

/** Fixed scatter positions in the hero's 1136×500 scene space. */
export const HERO_SLOTS = [
  { left: 40, top: 36, rot: -6 },
  { left: 300, top: 8, rot: 4 },
  { left: 880, top: 30, rot: 7 },
  { left: 120, top: 150, rot: -3 },
  { left: 650, top: 118, rot: 2 },
  { left: 940, top: 150, rot: 5 },
] as const;

/** Convergence target: the branded chip's dot, in scene coordinates. */
export const CHIP_ANCHOR = { x: 568, y: 258 } as const;

/** Per-channel results — rail measure bars, dashboard, hero scoreboard. */
export const CHANNEL_BARS = [
  { name: "newsletter", n: "1,284", w: 100, color: DATA_BLUE },
  { name: "instagram-bio", n: "942", w: 73, color: DATA_VIOLET },
  { name: "flyer-qr", n: "978", w: 76, color: DATA_GREEN },
  { name: "partner", n: "402", w: 31, color: DATA_AMBER },
] as const;

/** Hero scoreboard resting bar widths (%) — the reduced-motion state. */
export const HERO_BAR_REST = [62, 45, 70, 24] as const;

/** Lifecycle rail — the artifact pane's status chip per stage. */
export const RAIL_STATUS = [
  { label: "READY", sub: "link created · ready to share", color: DATA_AMBER },
  { label: "LIVE", sub: "4 routing rules active", color: "var(--color-brand-600)" },
  { label: "LIVE", sub: "counting every channel", color: "var(--color-brand-600)" },
  { label: "LIVE", sub: "tuned mid-run · same URL", color: "var(--color-brand-600)" },
] as const;

export const RAIL_STAGES = ["PLAN", "LAUNCH", "MEASURE", "OPTIMIZE"] as const;

/**
 * Plan — the URL a newsletter visitor lands on. The campaign tag is set on
 * the destination once with the dashboard's UTM builder (backend#478 — it
 * reaches the destination page and rii.link's Destination tags in analytics);
 * utm_source rides on the newsletter's copy of the short URL
 * (LAUNCH_CARDS[0].tag), is read by rii.link at click time and passed on by
 * the redirect's "Append original query params" switch (off by default;
 * routing appends it after the destination's own query).
 */
export const PLAN_URL_PARTS = [
  { text: "spring.acme.com/sale", label: "DESTINATION", on: false },
  { text: "?utm_campaign=spring-sale", label: "CAMPAIGN · ONCE", on: true },
  { text: "&utm_source=newsletter", label: "SOURCE · PER CHANNEL", on: true },
] as const;

/**
 * Launch — the 2×2 channel matrix. Icons live in the section component.
 * `tag` is the query each channel's copy of the short URL carries — typed by
 * the marketer (or set as the QR code's "Additional Parameters"), never
 * generated by rii.link. Analytics reads it from the clicked URL.
 */
export const LAUNCH_CARDS = [
  {
    name: "Newsletter",
    tag: "?utm_source=newsletter",
    color: DATA_BLUE,
    lands: "Desktop readers match no rule, so they land on the sale page — the default.",
  },
  {
    name: "Instagram bio",
    tag: "?utm_source=instagram-bio",
    color: DATA_VIOLET,
    lands: "iOS taps route straight to the App Store listing; Android taps, to Play Store.",
  },
  {
    name: "Flyer QR",
    tag: "?utm_source=flyer-qr",
    color: DATA_GREEN,
    lands: "Scans from Germany land on the German page, /de — same poster, local door.",
  },
  {
    name: "Partner post",
    tag: "?utm_source=partner",
    color: DATA_AMBER,
    lands: "On weekends the same URL routes to the partner's store page — one time rule.",
  },
] as const;

/** The four rules on the sample link — ART_ROWS "Routing" names their kinds. */
export const RULE_CHIPS = [
  "iOS scan → App Store",
  "Android scan → Play Store",
  "geo DE → /de landing",
  "weekends → partner page",
] as const;

/**
 * Measure — dashboard KPI row. Every card exists in the real analytics page
 * (frontend-web-user app/(app)/analytics/page.tsx): the Human Requests,
 * Mobile Traffic and Top Browser stat cards, plus the "QR scans · % of
 * visits" row of the Traffic origin panel. The Processing Time card is left
 * out so the mock states no latency number.
 */
export const MOBILE_REQUESTS = 2560; // 71% of TOTAL_EVENTS

export const KPIS = [
  { label: "Human requests", value: TOTAL_FMT, sub: "212 known bots excluded", accent: false },
  { label: "Mobile traffic", value: "71%", sub: "2,560 mobile requests", accent: false },
  { label: "Top browser", value: "Safari", sub: "Most popular browser", accent: false },
  { label: "QR scans", value: "978", sub: "27.1% of visits", accent: false },
] as const;

/**
 * Measure — daily sparkline for the dashboard's 30-day period (the app offers
 * 24h / 7d / 30d only), 0–80 scale in a 260×84 viewBox.
 */
export const SPARK = [
  10, 12, 11, 15, 18, 16, 20, 24, 22, 26, 30, 28, 34, 58, 46,
  40, 38, 42, 44, 41, 47, 52, 49, 50, 55, 53, 58, 61, 57, 60,
] as const;

export const sparkPoints = (): string => {
  const step = 260 / (SPARK.length - 1);
  return SPARK.map((v, i) => `${+(i * step).toFixed(2)},${80 - v}`).join(" ");
};

/**
 * Optimize — five read-out tiles, real status on each. Request logs and MCP
 * reach every link; the REST API and click stream only see links created
 * through the public API. Webhooks carry their lib/roadmap.ts date. There are
 * no GA4 / Meta / Zapier / Slack connectors, so they get no tile.
 */
export type TilePill = "live" | "roadmap";

export const OPT_TILES: {
  name: string;
  color: string;
  pill: TilePill;
  /** Required for roadmap tiles — the pill shows that item's `promised` date. */
  roadmap?: RoadmapId;
  desc: string;
}[] = [
  {
    name: "Request logs",
    color: DATA_GREEN,
    pill: "live",
    desc: "Every click with path, device and country — a live view refreshes every 3 s.",
  },
  {
    name: "MCP",
    color: DATA_VIOLET,
    pill: "live",
    desc: "Ask your AI agent for one link's clicks, split by utm_source, country or device.",
  },
  {
    name: "REST API",
    color: DATA_BLUE,
    pill: "live",
    desc: "Stats by country, device, referrer or rule for links you create over the API.",
  },
  {
    name: "Click stream",
    color: DATA_CYAN,
    pill: "live",
    desc: "Each click on your API-created links as a server-sent event, UTMs included.",
  },
  {
    name: "Webhooks",
    color: DATA_AMBER,
    pill: "roadmap",
    roadmap: "webhooks",
    desc: "Push delivery of every click to your own endpoint — on the roadmap.",
  },
];

/**
 * Deterministic decorative QR (29×29 with real finder corners) — the same
 * artifact on the rail pane and the launch flyer. Matches the site's other
 * decorative QR mocks (DomainWinsSection); a real code would point visitors
 * at a domain we don't own.
 */
export const QR_PATH: string = (() => {
  let seed = 42;
  const rnd = () => (seed = (seed * 1103515245 + 12345) % 2147483648) / 2147483648;
  const cells: [number, number][] = [];
  const finder = (fx: number, fy: number) => {
    for (let y = 0; y < 7; y++)
      for (let x = 0; x < 7; x++)
        if (x === 0 || x === 6 || y === 0 || y === 6 || (x >= 2 && x <= 4 && y >= 2 && y <= 4))
          cells.push([fx + x, fy + y]);
  };
  finder(0, 0);
  finder(22, 0);
  finder(0, 22);
  const inFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x > 20 && y < 8) || (x < 8 && y > 20);
  for (let y = 0; y < 29; y++)
    for (let x = 0; x < 29; x++) if (!inFinder(x, y) && rnd() < 0.44) cells.push([x, y]);
  return cells.map(([x, y]) => `M${x} ${y}h1v1h-1z`).join("");
})();

/** The branded link, monospace, brand-colored domain — used everywhere. */
export const LINK_DOMAIN = "go.acme.com";
export const LINK_SLUG = "/spring-sale";
